import { EnvironmentOutlined, FileTextOutlined, StarOutlined, TagOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { Dropdown, theme } from 'antd';
import type { ReactNode } from 'react';
import { AisPrintKind } from '../graphql/generated/graphql';
import { AIS_SESSION_INFO_QUERY } from './AisSessionInfo.gql';
import { useSocketPrint } from './useAisCapabilities';
import { useAisIdentity } from './useAisIdentity';

const KINDS: { kind: AisPrintKind; title: string; icon: ReactNode; optional: boolean }[] = [
  { kind: AisPrintKind.LABEL, title: 'Etiketten', icon: <TagOutlined />, optional: false },
  { kind: AisPrintKind.SPECIAL_LABEL, title: 'Spezial', icon: <StarOutlined />, optional: true },
  { kind: AisPrintKind.A4, title: 'A4', icon: <FileTextOutlined />, optional: false },
];

// The AIS session this login prints to; a newer link switches it. Shown only where printing goes there
export const AisSessionMenu = () => {
  const { token } = theme.useToken();
  const aisIdentity = useAisIdentity();
  const socketPrint = useSocketPrint();
  const shown = !!aisIdentity && socketPrint;
  const { data } = useQuery(AIS_SESSION_INFO_QUERY, { skip: !shown });
  const session = data?.aisSessionInfo;

  if (!shown || !session) {
    return null;
  }

  const styles = {
    trigger: css`
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      max-width: 200px;
      margin-right: ${token.marginLG}px;
      font-size: ${token.fontSizeSM}px;
      color: inherit;
      cursor: pointer;

      > span {
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `,
    status: css`
      display: inline-flex;
      align-items: center;
      gap: ${token.marginXS}px;
    `,
    dimmed: css`
      opacity: 0.75;
    `,
    dot: css`
      flex: none;
      width: ${token.sizeXS}px;
      height: ${token.sizeXS}px;
      border-radius: 50%;
      background: ${token.colorSuccess};
    `,
    panel: css`
      width: 320px;
      overflow: hidden;
      background: ${token.colorBgElevated};
      border-radius: ${token.borderRadiusLG}px;
      box-shadow: ${token.boxShadowSecondary};
    `,
    head: css`
      display: flex;
      flex-direction: column;
      gap: ${token.marginXXS}px;
      padding: ${token.padding}px;
      background: color-mix(in srgb, ${token.colorPrimary} 8%, ${token.colorBgElevated});
    `,
    headStatus: css`
      font-size: ${token.fontSizeSM}px;
      font-weight: 500;
      color: ${token.colorPrimaryText};
    `,
    room: css`
      display: flex;
      align-items: center;
      gap: ${token.marginXS}px;
      font-size: ${token.fontSizeLG}px;
      font-weight: 600;
      color: ${token.colorText};

      .anticon {
        color: ${token.colorPrimary};
      }
    `,
    hint: css`
      font-size: ${token.fontSizeSM}px;
      color: ${token.colorTextSecondary};
    `,
    list: css`
      margin: 0;
      padding: ${token.paddingXS}px ${token.padding}px ${token.padding}px;
      list-style: none;
    `,
    row: css`
      display: flex;
      align-items: center;
      gap: ${token.marginSM}px;
      padding: ${token.paddingSM}px 0;
      color: ${token.colorTextSecondary};

      & + & {
        border-top: ${token.lineWidth}px solid ${token.colorBorderSecondary};
      }
    `,
    name: css`
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: ${token.colorText};
    `,
    kind: css`
      flex: none;
      padding: 0 ${token.paddingXS}px;
      border-radius: ${token.borderRadiusSM}px;
      background: ${token.colorFillTertiary};
      font-size: ${token.fontSizeSM}px;
      color: ${token.colorTextSecondary};
    `,
  };

  const rows = KINDS.flatMap(({ kind, title, icon, optional }) => {
    const names = session.printers.filter(printer => printer.kinds.includes(kind)).map(printer => printer.name);
    if (optional && names.length === 0) {
      return [];
    }
    return (names.length > 0 ? names : ['–']).map((name, index) => (
      <li key={`${kind}-${index}`} className={styles.row}>
        {icon}
        <span className={styles.name}>{name}</span>
        <span className={styles.kind}>{title}</span>
      </li>
    ));
  });

  const panel = (
    <section aria-label="AIS-Sitzung" className={styles.panel}>
      <div className={styles.head}>
        <span className={`${styles.status} ${styles.headStatus}`}>
          <span className={styles.dot} />
          Mit dem AIS verbunden
        </span>
        {session.roomName && (
          <span className={styles.room}>
            <EnvironmentOutlined />
            {session.roomName}
          </span>
        )}
        <span className={styles.hint}>Ausdrucke gehen an die Drucker dieses Raums.</span>
      </div>
      <ul className={styles.list}>{rows}</ul>
    </section>
  );

  return (
    <Dropdown popupRender={() => panel} trigger={['click']} arrow placement="bottomRight">
      <div className={styles.trigger}>
        <span className={`${styles.status} ${styles.dimmed}`}>
          <span className={styles.dot} />
          AIS verbunden
        </span>
        {session.roomName && <span>{session.roomName}</span>}
      </div>
    </Dropdown>
  );
};
